import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { zql } from '../schema.js';
import { pickActorFromUserActions, pickTimestampFromUserActions, titleCase } from '../util/index.js';
import { assertCan, EventLogs } from '../utils.js';
import { DeleteResourceSchema, EventLogResources, UpdateResourceStatusSchema, addEventLog } from './util.js';
export const DrugsCategorySchema = z.object({
    name: z.string().trim().nonempty(),
    description: z.string(),
    status: z.enum(['active', 'inactive']),
});
export const AddDrugsCategorySchema = DrugsCategorySchema.extend({
    subcategories: z.array(DrugsCategorySchema),
});
export const UpdateDrugsCategorySchema = DrugsCategorySchema.partial().extend({
    subcategories: z.array(DrugsCategorySchema.extend({
        // id could be undefined if new subcategory added
        id: z.uuidv7().optional(),
    })),
    id: z.uuidv7().nonempty(),
});
function createSubcategory(tx, subcategory, userActions) {
    const newSubcategory = {
        id: uuidv7(),
        name: subcategory.name,
        description: subcategory.description,
        status: subcategory.status,
        parentId: subcategory.parentId,
        ...userActions,
    };
    const promises = [tx.mutate.drugsCategories.insert(newSubcategory)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newSubcategory);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.drugsCategory,
            resourceId: newSubcategory.id,
            action: 'create',
            patches: patches,
            performedAt: pickTimestampFromUserActions(userActions),
            performedBy: pickActorFromUserActions(userActions),
            domain: 'configuration',
            event: EventLogs.SUBCATEGORY_CREATED,
            status: 'OK',
            details: {
                id: newSubcategory.id,
                name: newSubcategory.name,
            },
        }));
    }
    return promises;
}
async function findById(tx, categoryId) {
    return await tx.run(zql.drugsCategories.where('id', '=', categoryId).related('children').one());
}
export const drugsCategories = {
    add: defineMutator(AddDrugsCategorySchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'basic_data.manage');
        const existingCategory = await tx.run(zql.drugsCategories.where('name', args.name).one());
        if (existingCategory)
            throw new Error('A category with this name already exists');
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const newCategory = {
            id: uuidv7(),
            name: titleCase(args.name),
            description: args.description,
            status: args.status,
            ...userActions,
        };
        await tx.mutate.drugsCategories.insert(newCategory);
        if (tx.location === 'server') {
            const patches = createPatch({}, newCategory);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugsCategory,
                resourceId: newCategory.id,
                action: 'create',
                domain: 'configuration',
                event: EventLogs.CATEGORY_CREATED,
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    name: newCategory.name,
                    id: newCategory.id,
                },
            }, ctx);
        }
        await Promise.all(args.subcategories.flatMap(subcategory => createSubcategory(tx, { ...subcategory, parentId: newCategory.id }, userActions)));
    }),
    update: defineMutator(UpdateDrugsCategorySchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'basic_data.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not Found');
        if (args.name) {
            const existingCategory = await tx.run(zql.drugsCategories
                // check for parent categories with the same name
                .where('name', '=', args.name)
                .where('id', '!=', args.id)
                .where('parentId', 'IS', null)
                .one());
            if (existingCategory)
                throw new Error('A category with this name already exists');
        }
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        const updatedCategory = {
            id: args.id,
            name: titleCase(args.name),
            description: args.description,
            status: args.status,
            ...userActions,
        };
        await tx.mutate.drugsCategories.update(updatedCategory);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedCategory);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugsCategory,
                resourceId: args.id,
                action: 'update',
                domain: 'configuration',
                event: EventLogs.CATEGORY_UPDATED,
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                details: {
                    name: updatedCategory.name,
                    id: updatedCategory.id,
                },
            }, ctx);
        }
        if (args.subcategories) {
            const subcategoryPromises = args.subcategories.flatMap(subcategory => {
                if (subcategory.id) {
                    const promises = [];
                    // update subcategory
                    const updatedSubcategory = {
                        id: subcategory.id,
                        name: subcategory.name,
                        description: subcategory.description,
                        status: subcategory.status,
                        ...userActions,
                    };
                    promises.push(tx.mutate.drugsCategories.update(updatedSubcategory));
                    if (tx.location === 'server') {
                        const patches = createPatch({}, updatedSubcategory);
                        promises.push(addEventLog(tx, {
                            id: uuidv7(),
                            resource: EventLogResources.drugsCategory,
                            resourceId: subcategory.id,
                            action: 'update',
                            patches: patches,
                            performedBy: ctx.user.id,
                            performedAt: now,
                            domain: 'configuration',
                            event: EventLogs.SUBCATEGORY_UPDATED,
                            status: 'OK',
                            details: {
                                id: updatedSubcategory.id,
                                name: updatedSubcategory.name,
                            },
                        }, ctx));
                    }
                    return promises;
                }
                return createSubcategory(tx, { ...subcategory, parentId: args.id }, userActions);
            });
            await Promise.all(subcategoryPromises);
        }
        // find and delete subcategories
        const currentSubcategoriesIds = new Set(args.subcategories.map(s => s.id));
        const deletedSubcategories = prev.children?.filter(subcategory => {
            return !currentSubcategoriesIds.has(subcategory.id);
        });
        if (deletedSubcategories.length) {
            await Promise.all(deletedSubcategories.flatMap(subcategory => {
                const updatedSubcategory = {
                    id: subcategory.id,
                    deletedAt: userActions.updatedAt,
                    deletedBy: userActions.updatedBy,
                };
                const promises = [tx.mutate.drugsCategories.update(updatedSubcategory)];
                if (tx.location === 'server') {
                    const patches = createPatch(subcategory, updatedSubcategory);
                    promises.push(addEventLog(tx, {
                        id: uuidv7(),
                        resource: EventLogResources.drugsCategory,
                        resourceId: updatedSubcategory.id,
                        action: 'delete',
                        patches: patches,
                        performedBy: updatedSubcategory.deletedBy,
                        performedAt: updatedSubcategory.deletedAt,
                        domain: 'configuration',
                        event: EventLogs.SUBCATEGORY_DELETED,
                        status: 'WARNING',
                        details: {
                            id: updatedSubcategory.id,
                            name: subcategory.name,
                        },
                    }, ctx));
                }
                return promises;
            }));
        }
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'basic_data.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedCategory = {
            id: args.id,
            status: args.status,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.drugsCategories.update(updatedCategory);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedCategory);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugsCategory,
                resourceId: args.id,
                domain: 'configuration',
                event: args.status === 'inactive' ? EventLogs.CATEGORY_DEACTIVATED : EventLogs.CATEGORY_REACTIVATED,
                action: 'update',
                patches: patches,
                performedBy: updatedCategory.updatedBy,
                performedAt: updatedCategory.updatedAt,
                status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                details: {
                    name: prev.name,
                    id: prev.id,
                },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'basic_data.manage');
        const prev = await findById(tx, args.id);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedCategory = {
            id: args.id,
            name: `${prev.name}_deleted_${now}`,
            deletedAt: now,
            deletedBy: ctx.user.id,
        };
        await tx.mutate.drugsCategories.update(updatedCategory);
        if (tx.location === 'server') {
            const patches = createPatch({}, updatedCategory);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.drugsCategory,
                resourceId: args.id,
                domain: 'configuration',
                event: EventLogs.CATEGORY_DELETED,
                status: 'WARNING',
                action: 'delete',
                patches: patches,
                performedBy: updatedCategory.deletedBy,
                performedAt: updatedCategory.deletedAt,
                details: {
                    name: prev.name,
                    id: prev.id,
                },
            }, ctx);
        }
        await Promise.all(prev.children.flatMap(c => {
            const updatedSubcategory = {
                id: c.id,
                name: `${c.name}_deleted_${now}`,
                deletedAt: updatedCategory.deletedAt,
                deletedBy: updatedCategory.deletedBy,
            };
            const promises = [tx.mutate.drugsCategories.update(updatedSubcategory)];
            if (tx.location === 'server') {
                const patches = createPatch(c, updatedSubcategory);
                promises.push(addEventLog(tx, {
                    id: uuidv7(),
                    resource: EventLogResources.drugsCategory,
                    resourceId: updatedSubcategory.id,
                    action: 'delete',
                    patches: patches,
                    performedBy: updatedSubcategory.deletedBy,
                    performedAt: updatedSubcategory.deletedAt,
                    domain: 'configuration',
                    event: EventLogs.SUBCATEGORY_DELETED,
                    status: 'WARNING',
                    details: {
                        id: updatedSubcategory.id,
                        name: updatedSubcategory.name,
                    },
                }, ctx));
            }
            return promises;
        }));
    }),
};
//# sourceMappingURL=drugs-category.mutators.js.map